import { computed, type ComputedRef } from 'vue';
import { useGetPage } from './useGetPage';
import { useGetPageBlock } from './useGetPageBlock';
import { useGetPageSection } from './useGetPageSection';
import { useGetPageField } from './useGetPageField';
import { findFieldByKeyOrNull, findFieldInBlockOrNull, findFieldInDocumentOrNull } from '../utils/document.utils';
import { getFieldValue } from '../utils/field.accessors';

export interface CreateFieldGetterParams {
  registryId: string;
  sectionKey?: string;
  blockKey?: string;
}

export interface FieldGetterResult {
  /**
   * Function to get a field value by key
   */
  getField: <T = string>(fieldKey: string) => ComputedRef<T>;
  /**
   * Computed ref indicating if the section is visible
   * Returns false if section doesn't exist, is not loaded, or has show: false
   */
  isVisible: ComputedRef<boolean>;
}

/**
 * Creates a scoped field getter function to avoid repeated document traversals.
 * This optimization caches the section/block/page context to eliminate redundant
 * tree navigation when retrieving multiple fields from the same location.
 *
 * Performance benefit: Caches context lookup (section/block/page) once, avoiding
 * repeated document tree traversals for each field access.
 *
 * @param params - Common context (registryId, sectionKey, blockKey)
 * @returns Object with getField function and isVisible computed ref
 *
 * @example
 * const { getField, isVisible } = useCreateFieldGetter({
 *   registryId: REGISTRY_IDS.HOME,
 *   sectionKey: 'header',
 *   blockKey: 'main'
 * });
 * const color = getField('background_color');
 * const image = getField('background_image');
 *
 * if (isVisible.value) {
 *   // render section
 * }
 */
export const useCreateFieldGetter = ({
  registryId,
  sectionKey,
  blockKey,
}: CreateFieldGetterParams): FieldGetterResult => {
  let cachedSection: ReturnType<typeof useGetPageSection> | undefined;
  let cachedBlock: ReturnType<typeof useGetPageBlock> | undefined;
  let cachedPage: ReturnType<typeof useGetPage> | undefined;

  if (blockKey && sectionKey) {
    cachedSection = useGetPageSection({ registryId, sectionKey, blockKey });
  } else if (blockKey) {
    cachedBlock = useGetPageBlock({ registryId, blockKey });
  } else if (sectionKey) {
    cachedSection = useGetPageSection({ registryId, sectionKey });
  } else {
    cachedPage = useGetPage({ registryId });
  }

  const isVisible = computed(() => {
    if (cachedSection?.value) {
      return cachedSection.value.show !== false;
    }
    if (cachedBlock || cachedPage) {
      return true;
    }
    return false;
  });

  const getField = <T = string>(fieldKey: string): ComputedRef<T> => {
    return computed(() => {
      // A field added to the registry after a document was stored won't exist in that
      // stored document. Fall back to an empty value instead of throwing so a single
      // missing field can't crash the whole page — callers treat '' as "use default"
      // (e.g. required flags), and registry defaults are otherwise filled upstream by
      // useGetPage (buildDocumentFromTemplate).
      if (cachedSection?.value) {
        const field = findFieldByKeyOrNull(cachedSection.value, fieldKey);
        return field ? getFieldValue<T>(field) : ('' as T);
      }

      if (cachedBlock?.value) {
        const field = findFieldInBlockOrNull(cachedBlock.value, fieldKey);
        return field ? getFieldValue<T>(field) : ('' as T);
      }

      if (cachedPage?.value) {
        const field = findFieldInDocumentOrNull(cachedPage.value, fieldKey);
        return field ? getFieldValue<T>(field) : ('' as T);
      }

      const fieldRef = useGetPageField<T>({ registryId, fieldKey, sectionKey, blockKey });

      if (!fieldRef.value) {
        return '' as T;
      }

      return fieldRef.value;
    });
  };

  return {
    getField,
    isVisible,
  };
};
